import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserProfileModal({ open, onOpenChange }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const email = user?.email ?? "";

  const initialName =
    (user?.user_metadata?.full_name as string | undefined) ??
    (user?.user_metadata?.name as string | undefined) ??
    "";

  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase.auth.updateUser({
      data: { full_name: name.trim(), name: name.trim() },
    });

    if (error) {
      setSaving(false);
      toast({ title: "Error al guardar", description: error.message, variant: "destructive" });
      return;
    }

    // Forzar refresh de sesión para que useAuth re-emita user con metadata fresca
    await supabase.auth.refreshSession();

    // Invalidar vistas que listan usuarios por nombre (Equipo / Consola Master)
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["team-members"] }),
      queryClient.invalidateQueries({ queryKey: ["org-members"] }),
      queryClient.invalidateQueries({ queryKey: ["all-users-global"] }),
      queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
    ]);

    setSaving(false);
    toast({ title: "Perfil actualizado", description: "Tu nombre se ha guardado correctamente." });
    onOpenChange(false);
  };

  const handleResetPassword = async () => {
    if (!email) return;
    setResetting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setResetting(false);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({
      title: "Correo enviado",
      description: `Hemos enviado un enlace para restablecer tu contraseña a ${email}.`,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Mi Perfil</DialogTitle>
          <DialogDescription>
            Actualiza tu información personal y gestiona tu seguridad.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="profile-name">Nombre</Label>
            <Input
              id="profile-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tu nombre completo"
              maxLength={80}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="profile-email">Correo electrónico</Label>
            <Input id="profile-email" value={email} disabled className="bg-muted/40" />
            <p className="text-[11px] text-muted-foreground">
              El correo no se puede modificar desde aquí.
            </p>
          </div>

          <div className="rounded-md border border-border bg-muted/30 p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">Contraseña</p>
              <p className="text-xs text-muted-foreground truncate">
                Te enviaremos un enlace seguro a tu correo.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetPassword}
              disabled={resetting || !email}
            >
              {resetting ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <KeyRound className="mr-2 h-3.5 w-3.5" />
              )}
              Cambiar
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving || name.trim() === initialName.trim()}>
            {saving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
